// src/components/portfolioupload.jsx — STYLIST REAL-WORLD WORK GALLERY
import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions'; // Added for AI Trigger
import { storage, db, functions } from '../firebase'; // Ensure functions is exported from your firebase.js
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function PortfolioUpload() {
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();

  const toastStyle = {
    style: { background: NAVY, color: GOLD, border: `1px solid ${GOLD}`, borderRadius: '12px' }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!imageFile || !description.trim()) {
      toast.error("Please provide both a photo and description", toastStyle);
      return;
    }

    setUploading(true);
    const loadingToast = toast.loading("Processing 3D Geometry...", toastStyle);

    try {
      // 1. Upload Image to Storage
      const fileRef = storageRef(storage, `portfolios/${user.uid}/${Date.now()}-${imageFile.name}`);
      await uploadBytes(fileRef, imageFile);
      const url = await getDownloadURL(fileRef);

      // 2. Trigger AI 3D Reconstruction (Using your existing renderHair function)
      // This ensures the hairstyle becomes a 3D object for the Cranial View
      const renderHairAI = httpsCallable(functions, 'renderHair');
      const aiResponse = await renderHairAI({ 
        image: url, 
        style: description.trim() 
      });

      const styleModelUrl = aiResponse.data.url;

      // 3. Add to Stylist's Personal Portfolio Collection with 3D Reference
      await addDoc(collection(db, 'barbers', user.uid, 'portfolio'), {
        imageUrl: url,
        styleModelUrl: styleModelUrl, // The 3D .glb file generated by Replicate
        description: description.trim(),
        promotionStatus: 'pending_approval', // Matches Dashboard logic
        createdAt: serverTimestamp(),
      });

      toast.success("3D Masterpiece Saved! ◈", { id: loadingToast, ...toastStyle });
      
      setTimeout(() => navigate('/stylist/dashboard'), 1500);
    } catch (err) {
      console.error(err);
      toast.error("AI 3D Processing failed. Check your Replicate quota.", { id: loadingToast, ...toastStyle });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ color: GOLD, fontSize: '1.8rem', fontWeight: '800', marginBottom: '10px' }}>
          SERVICE PORTFOLIO
        </h2>
        <p style={{ opacity: 0.7, marginBottom: '2rem' }}>Generates 3D Geometry automatically</p>

        {/* Upload Area */}
        <div 
          onClick={() => document.getElementById('port-file').click()}
          style={{
            ...dropZoneStyle,
            borderColor: preview ? GOLD : 'rgba(255,255,255,0.2)'
          }}
        >
          {preview ? (
            <img src={preview} style={previewStyle} alt="Preview" />
          ) : (
            <div style={{ padding: '40px' }}>
              <span style={{ fontSize: '3rem' }}>📸</span>
              <p style={{ fontWeight: 'bold', marginTop: '10px' }}>TAP TO UPLOAD WORK</p>
            </div>
          )}
          <input 
            id="port-file" 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
            style={{ display: 'none' }} 
          />
        </div>

        <textarea
          placeholder="Describe the service (e.g. Skin fade with beard sculpt...)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={textareaStyle}
        />

        <button 
          onClick={handleUpload} 
          disabled={uploading} 
          style={{
            ...btnStyle,
            opacity: uploading ? 0.6 : 1
          }}
        >
          {uploading ? 'GENERATING 3D MODEL...' : 'ADD TO PORTFOLIO'}
        </button>

        <button onClick={() => navigate(-1)} style={backBtnStyle}>Cancel</button>
      </div>
    </div>
  );
}

// STYLES PRESERVED EXACTLY AS PROVIDED
const containerStyle = { minHeight: '100vh', background: NAVY, display: 'grid', placeItems: 'center', padding: '20px', fontFamily: 'Montserrat, sans-serif' };
const cardStyle = { width: '100%', maxWidth: '450px', background: 'rgba(255,255,255,0.03)', padding: '40px', borderRadius: '30px', border: '1px solid rgba(184,134,11,0.2)', textAlign: 'center' };
const dropZoneStyle = { border: '2px dashed', borderRadius: '20px', cursor: 'pointer', overflow: 'hidden', marginBottom: '20px', transition: '0.3s' };
const previewStyle = { width: '100%', height: '300px', objectFit: 'cover' };
const textareaStyle = { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '15px', padding: '15px', color: 'white', fontSize: '0.9rem', marginBottom: '20px', resize: 'none', outline: 'none' };
const btnStyle = { width: '100%', background: GOLD, color: 'black', padding: '18px', borderRadius: '50px', border: 'none', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer' };
const backBtnStyle = { background: 'none', border: 'none', color: '#888', marginTop: '15px', cursor: 'pointer' };
